import { useState } from 'react';

interface UseAuthFormReturn<T> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  success: string;
  setSuccess: React.Dispatch<React.SetStateAction<string>>;
  resetForm: () => void;
  clearMessages: () => void;
}

/**
 * Custom hook for managing authentication form state
 */
export function useAuthForm<T extends Record<string, any>>(
  initialState: T
): UseAuthFormReturn<T> {
  const [formData, setFormData] = useState<T>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const resetForm = () => {
    setFormData(initialState);
    setIsLoading(false);
    setError('');
    setSuccess('');
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  return {
    formData,
    setFormData,
    handleInputChange,
    isLoading,
    setIsLoading,
    error,
    setError,
    success,
    setSuccess,
    resetForm,
    clearMessages,
  };
}
